import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { buildSiteHierarchyGroups } from '@/lib/catalogSiteTree';
import { SiteLevelBadge } from '@/components/SiteLevelBadge';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { inputClass, labelClass, inlineSuccessClass, inlineErrorClass } from '@/components/ui/inputStyles';

const PER_PAGE = 15;
const LOG_PER_PAGE = 25;

type SiteBrief = {
  id: number;
  site_code: string;
  site_name: string;
  enabled: boolean;
  parent_site_id?: number | null;
};

type PromotionAreaBrief = { id: number; name: string; enabled: boolean };

type MasterRow = {
  id: number;
  phone: string;
  display_name: string;
  enabled: boolean;
  remark: string;
  created_at: string;
  sites: SiteBrief[];
  site_ids: number[];
  promotion_areas?: PromotionAreaBrief[];
  promotion_area_ids?: number[];
};

type LogRow = {
  id: number;
  master_id: number;
  phone: string;
  display_name: string;
  action: string;
  site_id: number | null;
  entry_id: number | null;
  detail: string;
  remote_ip: string | null;
  created_at: string;
};

const ACTION_LABELS: Record<string, string> = {
  login: '登录',
  logout: '退出',
  login_fail: '登录失败',
  switch_site: '切换站点',
  update_entry: '编辑信息',
  create_entry: '添加信息',
  delete_entry: '删除信息',
  change_password: '修改密码',
  update_profile: '修改资料',
  admin_create: '管理员创建',
  admin_update: '管理员修改',
  admin_set_sites: '分配站点',
  admin_set_areas: '分配推广区域',
  admin_delete: '管理员删除',
  create_mp_entry: '添加微推广',
  update_mp_entry: '编辑微推广',
  delete_mp_entry: '删除微推广',
};

export function SiteMastersPage() {
  const [tab, setTab] = useState<'masters' | 'logs'>('masters');
  const [items, setItems] = useState<MasterRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const [allSites, setAllSites] = useState<SiteBrief[]>([]);
  const [allPromotionAreas, setAllPromotionAreas] = useState<PromotionAreaBrief[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<MasterRow | null>(null);
  const [formPhone, setFormPhone] = useState('');
  const [formName, setFormName] = useState('');
  const [formRemark, setFormRemark] = useState('');
  const [formPass, setFormPass] = useState('');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formSiteIds, setFormSiteIds] = useState<number[]>([]);
  const [formAreaIds, setFormAreaIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [isCreate, setIsCreate] = useState(false);

  const [logs, setLogs] = useState<LogRow[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logLoading, setLogLoading] = useState(false);
  const [logQ, setLogQ] = useState('');
  const [logAppliedQ, setLogAppliedQ] = useState('');
  const [logMasterFilter, setLogMasterFilter] = useState<number | ''>('');

  const loadSites = useCallback(async () => {
    try {
      const [sitesR, areasR] = await Promise.all([
        apiFetch<{ ok: boolean; items: SiteBrief[] }>('list_sites', {
          method: 'GET',
          query: { page: 1, per_page: 500 },
        }),
        apiFetch<{ ok: boolean; items: PromotionAreaBrief[] }>('list_promotion_areas', {
          method: 'GET',
          query: { page: 1, per_page: 200 },
        }),
      ]);
      if (sitesR.ok) setAllSites(sitesR.items ?? []);
      if (areasR.ok) setAllPromotionAreas(areasR.items ?? []);
    } catch {
      /* ignore */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; items: MasterRow[]; total: number; page: number; per_page: number }>(
        'list_site_masters',
        {
          method: 'GET',
          query: { page, per_page: PER_PAGE, ...(appliedQ ? { q: appliedQ } : {}) },
        }
      );
      if (r.ok) {
        setItems(r.items);
        setTotal(r.total);
      } else {
        setErr('加载站长列表失败');
      }
    } catch {
      setErr('加载站长列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, appliedQ]);

  const loadLogs = useCallback(async () => {
    setLogLoading(true);
    try {
      const r = await apiFetch<{ ok: boolean; items: LogRow[]; total: number }>('list_site_master_logs', {
        method: 'GET',
        query: {
          page: logPage,
          per_page: LOG_PER_PAGE,
          ...(logAppliedQ ? { q: logAppliedQ } : {}),
          ...(logMasterFilter !== '' ? { master_id: logMasterFilter } : {}),
        },
      });
      if (r.ok) {
        setLogs(r.items);
        setLogTotal(r.total);
      }
    } catch {
      /* ignore */
    } finally {
      setLogLoading(false);
    }
  }, [logPage, logAppliedQ, logMasterFilter]);

  useEffect(() => {
    void loadSites();
  }, [loadSites]);

  useEffect(() => {
    if (tab === 'masters') void load();
  }, [tab, load]);

  useEffect(() => {
    if (tab === 'logs') void loadLogs();
  }, [tab, loadLogs]);

  function openCreate() {
    setIsCreate(true);
    setEditRow(null);
    setFormPhone('');
    setFormName('');
    setFormRemark('');
    setFormPass('');
    setFormEnabled(true);
    setFormSiteIds([]);
    setFormAreaIds([]);
    setEditOpen(true);
  }

  function openEdit(row: MasterRow) {
    setIsCreate(false);
    setEditRow(row);
    setFormPhone(row.phone);
    setFormName(row.display_name);
    setFormRemark(row.remark);
    setFormPass('');
    setFormEnabled(row.enabled);
    setFormSiteIds([...row.site_ids]);
    setFormAreaIds([...(row.promotion_area_ids ?? [])]);
    setEditOpen(true);
  }

  function toggleSiteId(id: number) {
    setFormSiteIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function toggleAreaId(id: number) {
    setFormAreaIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const siteHierarchyGroups = useMemo(() => buildSiteHierarchyGroups(allSites), [allSites]);

  function renderSiteCheckbox(site: SiteBrief, depth: 0 | 1, parentName?: string) {
    return (
      <label
        key={site.id}
        className={cn(
          'flex cursor-pointer items-start gap-2 rounded-lg py-1.5 text-sm hover:bg-slate-50/80',
          depth === 1 && 'ml-3 border-l-2 border-emerald-200/80 pl-3',
        )}
      >
        <input
          type="checkbox"
          className="mt-1 shrink-0"
          checked={formSiteIds.includes(site.id)}
          onChange={() => toggleSiteId(site.id)}
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <SiteLevelBadge depth={depth} />
            <span className="font-medium text-slate-800">{site.site_name}</span>
            <span className="text-slate-400">({site.site_code})</span>
            {!site.enabled ? <span className="text-amber-600">已禁用</span> : null}
          </span>
          {depth === 1 && parentName ? (
            <span className="mt-0.5 block text-xs text-slate-500">隶属一级站点「{parentName}」</span>
          ) : null}
        </span>
      </label>
    );
  }

  async function submitForm() {
    const phone = formPhone.trim();
    if (!/^1\d{10}$/.test(phone.replace(/\s/g, ''))) {
      setErr('请填写 11 位手机号');
      return;
    }
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      if (isCreate) {
        const r = await apiFetch<{ ok: boolean; err?: string; detail?: string; default_password?: string }>(
          'create_site_master',
          {
            method: 'POST',
            body: JSON.stringify({
              phone: phone.replace(/\s/g, ''),
              display_name: formName.trim(),
              remark: formRemark.trim(),
              site_ids: formSiteIds,
              promotion_area_ids: formAreaIds,
              ...(formPass.trim() !== '' ? { password: formPass.trim() } : {}),
            }),
          }
        );
        if (r.ok) {
          setMsg(
            r.default_password
              ? `已创建站长，初始密码为 ${r.default_password}，请告知对方登录后尽快修改。`
              : '已创建站长'
          );
          setEditOpen(false);
          void load();
        } else {
          setErr(r.detail ?? (r.err === 'phone_exists' ? '该手机号已存在' : '创建失败'));
        }
      } else if (editRow) {
        const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('update_site_master', {
          method: 'POST',
          body: JSON.stringify({
            id: editRow.id,
            phone: phone.replace(/\s/g, ''),
            display_name: formName.trim(),
            remark: formRemark.trim(),
            enabled: formEnabled,
            ...(formPass.trim() !== '' ? { password: formPass.trim() } : {}),
          }),
        });
        if (!r.ok) {
          setErr(r.detail ?? '保存失败');
          return;
        }
        const r2 = await apiFetch<{ ok: boolean }>('set_site_master_sites', {
          method: 'POST',
          body: JSON.stringify({ id: editRow.id, site_ids: formSiteIds }),
        });
        if (!r2.ok) {
          setErr('保存站点分配失败');
          return;
        }
        const r3 = await apiFetch<{ ok: boolean }>('set_site_master_promotion_areas', {
          method: 'POST',
          body: JSON.stringify({ id: editRow.id, promotion_area_ids: formAreaIds }),
        });
        if (r3.ok) {
          setMsg('已保存站长信息、站点与推广区域分配');
          setEditOpen(false);
          void load();
        } else {
          setErr('保存推广区域分配失败');
        }
      }
    } catch {
      setErr('保存失败');
    } finally {
      setSaving(false);
    }
  }

  async function deleteMaster(row: MasterRow) {
    if (!window.confirm(`确定删除站长「${row.phone}」？其操作日志将保留。`)) return;
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean }>('delete_site_master', {
        method: 'POST',
        body: JSON.stringify({ id: row.id }),
      });
      if (r.ok) {
        setMsg('已删除');
        void load();
      } else {
        setErr('删除失败');
      }
    } catch {
      setErr('删除失败');
    }
  }

  const masterPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const logPages = Math.max(1, Math.ceil(logTotal / LOG_PER_PAGE));

  return (
    <div className="space-y-6">
      <PageHeader
        title="站长管理"
        description="为站长分配站点（贴图信息）与微推广区域（与站点无关）。站长在手机端 /w/login 分别管理两类信息。"
      />

      <div className="flex flex-wrap gap-2 border-b border-slate-200/80 pb-1">
        <button
          type="button"
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${tab === 'masters' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          onClick={() => setTab('masters')}
        >
          站长账号
        </button>
        <button
          type="button"
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${tab === 'logs' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          onClick={() => setTab('logs')}
        >
          操作日志
        </button>
      </div>

      {msg ? <p className={inlineSuccessClass}>{msg}</p> : null}
      {err && !editOpen ? <p className={inlineErrorClass}>{err}</p> : null}

      {tab === 'masters' ? (
        <Card>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="flex-1 min-w-[200px]">
              <span className={labelClass}>搜索</span>
              <input
                className={inputClass}
                placeholder="手机号、称呼、备注"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (setAppliedQ(qInput.trim()), setPage(1))}
              />
            </label>
            <Button type="button" variant="secondary" onClick={() => (setAppliedQ(qInput.trim()), setPage(1))}>
              查询
            </Button>
            <Button type="button" onClick={openCreate}>
              新建站长
            </Button>
          </div>

          {loading ? (
            <Spinner label="加载中" />
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500">暂无站长，点击「新建站长」添加。</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500">
                    <th className="py-2 pr-3">手机号</th>
                    <th className="py-2 pr-3">称呼</th>
                    <th className="py-2 pr-3">管理站点</th>
                    <th className="py-2 pr-3">管理区域</th>
                    <th className="py-2 pr-3">状态</th>
                    <th className="py-2">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100">
                      <td className="py-3 pr-3 font-mono">{row.phone}</td>
                      <td className="py-3 pr-3">{row.display_name || '—'}</td>
                      <td className="py-3 pr-3">
                        {row.sites.length === 0 ? (
                          <span className="text-amber-600">未分配</span>
                        ) : (
                          <span className="text-slate-700">
                            {row.sites.map((s) => s.site_name).join('、')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        {(row.promotion_areas?.length ?? 0) === 0 ? (
                          <span className="text-amber-600">未分配</span>
                        ) : (
                          <span className="text-slate-700">
                            {row.promotion_areas!.map((a) => a.name).join('、')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        {row.enabled ? (
                          <span className="text-emerald-700">正常</span>
                        ) : (
                          <span className="text-red-600">已禁用</span>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(row)}>
                            编辑
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setLogMasterFilter(row.id);
                              setTab('logs');
                            }}
                          >
                            日志
                          </Button>
                          <Button type="button" variant="danger" size="sm" onClick={() => void deleteMaster(row)}>
                            删除
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <PaginationBar page={page} totalPages={masterPages} onPageChange={setPage} className="mt-4" />
          <p className="mt-2 text-xs text-slate-500">
            站长手机端入口：部署地址 + <code className="rounded bg-slate-100 px-1">/w/login</code>（可发给站长在微信中打开）
          </p>
        </Card>
      ) : (
        <Card>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="flex-1 min-w-[200px]">
              <span className={labelClass}>搜索日志</span>
              <input
                className={inputClass}
                placeholder="手机号、操作、详情"
                value={logQ}
                onChange={(e) => setLogQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (setLogAppliedQ(logQ.trim()), setLogPage(1))}
              />
            </label>
            <label className="min-w-[140px]">
              <span className={labelClass}>站长</span>
              <select
                className={inputClass}
                value={logMasterFilter === '' ? '' : String(logMasterFilter)}
                onChange={(e) => {
                  const v = e.target.value;
                  setLogMasterFilter(v === '' ? '' : parseInt(v, 10));
                  setLogPage(1);
                }}
              >
                <option value="">全部</option>
                {items.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.phone}
                  </option>
                ))}
              </select>
            </label>
            <Button type="button" variant="secondary" onClick={() => (setLogAppliedQ(logQ.trim()), setLogPage(1))}>
              查询
            </Button>
            {logMasterFilter !== '' ? (
              <Button type="button" variant="secondary" onClick={() => setLogMasterFilter('')}>
                清除站长筛选
              </Button>
            ) : null}
          </div>
          {logLoading ? (
            <Spinner label="加载日志" />
          ) : (
            <div className="space-y-2">
              {logs.map((l) => (
                <div
                  key={l.id}
                  className="rounded-xl border border-slate-200/90 bg-slate-50/50 px-4 py-3 text-sm"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-800">
                      {ACTION_LABELS[l.action] ?? l.action}
                    </span>
                    <span className="text-slate-500">{l.created_at}</span>
                    <span className="font-mono text-slate-600">{l.phone}</span>
                    {l.display_name ? <span className="text-slate-600">（{l.display_name}）</span> : null}
                  </div>
                  {l.detail ? <p className="mt-1 text-slate-700">{l.detail}</p> : null}
                  <p className="mt-1 text-xs text-slate-500">
                    {l.site_id ? `站点 #${l.site_id}` : ''}
                    {l.entry_id ? ` · 信息 #${l.entry_id}` : ''}
                    {l.remote_ip ? ` · IP ${l.remote_ip}` : ''}
                  </p>
                </div>
              ))}
              {logs.length === 0 ? <p className="text-slate-500">暂无日志</p> : null}
            </div>
          )}
          <PaginationBar page={logPage} totalPages={logPages} onPageChange={setLogPage} className="mt-4" />
        </Card>
      )}

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={isCreate ? '新建站长' : '编辑站长'}
        wide
      >
        {err && editOpen ? <p className={`${inlineErrorClass} mb-3`}>{err}</p> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="sm:col-span-1">
            <span className={labelClass}>手机号（登录账号）</span>
            <input
              className={inputClass}
              inputMode="numeric"
              autoComplete="tel"
              value={formPhone}
              onChange={(e) => setFormPhone(e.target.value)}
              placeholder="11 位手机号"
            />
          </label>
          <label>
            <span className={labelClass}>称呼（选填）</span>
            <input className={inputClass} value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="如：张师傅" />
          </label>
          <label className="sm:col-span-2">
            <span className={labelClass}>{isCreate ? '初始密码（留空则为 88888888）' : '重置密码（留空不修改）'}</span>
            <input
              className={inputClass}
              type="password"
              autoComplete="new-password"
              value={formPass}
              onChange={(e) => setFormPass(e.target.value)}
            />
          </label>
          {!isCreate ? (
            <label className="flex items-center gap-2 sm:col-span-2">
              <input type="checkbox" checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} />
              <span className="text-sm text-slate-700">账号启用</span>
            </label>
          ) : null}
          <label className="sm:col-span-2">
            <span className={labelClass}>管理员备注</span>
            <input className={inputClass} value={formRemark} onChange={(e) => setFormRemark(e.target.value)} />
          </label>
          <div className="sm:col-span-2">
            <span className={labelClass}>分配站点（可多选）</span>
            <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-slate-200 p-3">
              {allSites.length === 0 ? (
                <p className="text-sm text-slate-500">请先在「站点管理」创建站点</p>
              ) : (
                <div className="space-y-3">
                  {siteHierarchyGroups.map((g, gi) => {
                    const parent = g.parent?.site;
                    const parentName = parent?.site_name ?? '';
                    if (parent && g.children.length > 0) {
                      return (
                        <div
                          key={`grp-${gi}-${parent.id}`}
                          className="rounded-lg border border-slate-100 bg-slate-50/50 p-2"
                        >
                          {renderSiteCheckbox(parent, 0)}
                          <div className="mt-1 space-y-0.5">
                            <p className="mb-1 pl-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                              二级站点
                            </p>
                            {g.children.map((c) => renderSiteCheckbox(c.site, 1, parentName))}
                          </div>
                        </div>
                      );
                    }
                    if (parent) {
                      return <div key={`root-${parent.id}`}>{renderSiteCheckbox(parent, 0)}</div>;
                    }
                    return (
                      <div key={`orphan-${gi}`} className="space-y-0.5">
                        {g.children.map((c) => {
                          const pid = c.site.parent_site_id;
                          const pName = pid ? allSites.find((x) => x.id === pid)?.site_name : undefined;
                          return renderSiteCheckbox(c.site, 1, pName);
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-500">仅显示您有权限管理的站点；站长只能操作已勾选且处于启用状态的站点。</p>
          </div>
          <div className="sm:col-span-2">
            <span className={labelClass}>分配推广区域（可多选，与站点无关）</span>
            <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-teal-200/80 bg-teal-50/30 p-3">
              {allPromotionAreas.length === 0 ? (
                <p className="text-sm text-slate-500">请先在「微推广管理」创建推广区域</p>
              ) : (
                <div className="space-y-1">
                  {allPromotionAreas.map((a) => (
                    <label key={a.id} className="flex cursor-pointer items-center gap-2 rounded-lg py-1.5 text-sm hover:bg-white/80">
                      <input
                        type="checkbox"
                        checked={formAreaIds.includes(a.id)}
                        onChange={() => toggleAreaId(a.id)}
                      />
                      <span className="font-medium text-slate-800">{a.name}</span>
                      <span className="font-mono text-xs text-slate-500">#{a.id}</span>
                      {!a.enabled ? <span className="text-xs text-amber-700">已停用</span> : null}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-500">站长可在手机端管理这些区域下的图文/招聘推广信息（与金数据 Webhook 入库并列）。</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setEditOpen(false)}>
            取消
          </Button>
          <Button type="button" disabled={saving} onClick={() => void submitForm()}>
            {saving ? '保存中…' : '保存'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
